import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Trash2 } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '../../../components/ui/avatar';
import { Button } from '../../../components/ui/button';

/**
 * Mirrors `PHOTO_MAX_BYTES` in `onboarding_enrichment_service.py` — the wizard
 * photo travels inline on `/create-tenant`, and the server rejects anything
 * larger. Catching it here means the user hears about it before they submit.
 */
const MAX_SIZE_MB = 2;

/** Raster allowlist — kept in sync with `_ALLOWED_PHOTO_MIME` server-side. */
const ALLOWED_MIME = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];

interface WizardAvatarPickerProps {
  /** Initials shown when there is neither a picked file nor a remote photo. */
  initials: string;
  /** Alt text / accessible name source for the avatar image. */
  fullName: string;
  /** Remote photo (Gravatar, LinkedIn candidate) shown when no file is picked. */
  photoUrl: string | null;
  /** The locally-picked file, held until the tenant exists. */
  file: File | null;
  onFileChange: (file: File | null) => void;
}

/**
 * Avatar picker for the pre-tenant setup wizard.
 *
 * It deliberately does NOT use the shared `ImageUpload`: that component POSTs to
 * `/files` on selection, and `files.tenant_id` is NOT NULL — a `pending_setup`
 * user has no tenant yet, so the upload would fail. Instead the `File` is held
 * in wizard state (previewed from an object URL) and uploaded by
 * `SetupWizardPageV2` immediately after the tenant is created, the same
 * deferred-write-back shape the backend already uses for `avatar_photo_url`.
 */
export function WizardAvatarPicker({
  initials,
  fullName,
  photoUrl,
  file,
  onFileChange,
}: WizardAvatarPickerProps) {
  const { t } = useTranslation(['user_management', 'common']);
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    // Reset immediately so re-picking the same file still fires a change event.
    event.target.value = '';
    if (!picked) return;

    if (!ALLOWED_MIME.includes(picked.type)) {
      setError(t('setupWizardV2.profile.photoImageOnly'));
      return;
    }
    if (picked.size === 0 || picked.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(t('setupWizardV2.profile.photoTooLarge', { size: MAX_SIZE_MB }));
      return;
    }
    setError(null);
    onFileChange(picked);
  };

  const handleRemove = () => {
    setError(null);
    onFileChange(null);
  };

  const src = previewUrl ?? photoUrl ?? undefined;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative">
        {/* Mouse-only convenience: clicking the avatar opens the picker. The
            camera button below is the real, labelled, keyboard-reachable
            control, so this is hidden from the a11y tree to avoid a duplicate
            tab stop + duplicate accessible name. */}
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => inputRef.current?.click()}
          className="rounded-full"
          data-testid="wizard-avatar-picker"
        >
          <Avatar className="h-24 w-24">
            {src && <AvatarImage src={src} alt={fullName} />}
            <AvatarFallback className="text-xl">{initials}</AvatarFallback>
          </Avatar>
        </button>

        <div className="absolute -bottom-1 -end-1 flex gap-1">
          <Button
            type="button"
            size="icon"
            className="h-8 w-8 rounded-full touch:h-11 touch:w-11"
            onClick={() => inputRef.current?.click()}
            aria-label={src ? t('setupWizardV2.profile.replacePhoto') : t('setupWizardV2.profile.addPhoto')}
          >
            <Camera className="h-4 w-4" />
          </Button>
          {file && (
            <Button
              type="button"
              size="icon"
              variant="destructive"
              className="h-8 w-8 rounded-full touch:h-11 touch:w-11"
              onClick={handleRemove}
              aria-label={t('common:app.remove')}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        // Raster only — the server rejects SVG (it's served back inline and
        // would be a stored-XSS vector). Keep this in sync with
        // `_ALLOWED_PHOTO_MIME` in onboarding_enrichment_service.py.
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={handleSelect}
        className="hidden"
        data-testid="wizard-avatar-input"
      />

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
